import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import ValidatorCard from '../components/Validators/ValidatorCard';
import ValidatorMetrics from '../components/Validators/ValidatorMetrics';

const Validators: React.FC = () => {
  const { data: validators, isLoading } = useQuery({
    queryKey: ['validators'],
    queryFn: () => apiClient.getValidators(),
    refetchInterval: 15000,
  });

  const { data: validatorHealth, isLoading: healthLoading } = useQuery({
    queryKey: ['validatorHealth'],
    queryFn: () => apiClient.getValidatorHealthSummary(),
    refetchInterval: 10000, // More frequent updates for health data
  });

  if (isLoading || healthLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Use health data as primary source, merge with bridge data if available
  const healthStatuses = validatorHealth?.validatorStatuses || [];
  const bridgeData = validators || [];
  
  // Create a merged dataset that prioritizes health data
  const mergedValidators = healthStatuses.map(health => {
    const bridge = bridgeData.find(b => b.validator === health.validator);
    return {
      ...health,
      // Use health data for primary status
      isOnline: health.isHealthy,
      lastActiveTime: health.lastChecked,
      // Include bridge data if available
      totalApprovals: bridge?.totalApprovals || 0,
      successRate: bridge?.successRate || 0,
      // Preserve health-specific fields
      consecutiveFailures: health.consecutiveFailures,
      errorMessage: health.errorMessage,
      statusCode: health.statusCode,
    };
  });

  const onlineValidators = mergedValidators.filter(v => v.isOnline);
  const offlineValidators = mergedValidators.filter(v => !v.isOnline);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Bridge Validators</h1>
        <p className="text-gray-600 mt-1">Monitor validator performance and status</p>
      </div>

      {/* Validator Metrics Overview */}
      <ValidatorMetrics validators={mergedValidators || []} />

      {/* Online Validators */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Online Validators ({onlineValidators.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {onlineValidators.map((validator: any) => (
            <ValidatorCard 
              key={validator.validator} 
              validator={validator}
              status="online"
            />
          ))}
        </div>
      </div>

      {/* Offline Validators */}
      {offlineValidators.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Offline Validators ({offlineValidators.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offlineValidators.map((validator: any) => (
              <ValidatorCard 
                key={validator.validator} 
                validator={validator}
                status="offline"
              />
            ))}
          </div>
        </div>
      )}

      {/* No Validators Message */}
      {(!mergedValidators || mergedValidators.length === 0) && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No validators found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No validator health data is currently available.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Validators; 